import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import moment, { Moment } from "moment";

import { RootState } from "../../../appRedux/store";
import { CustomNotification } from "../../../util/CustomNotification";
import * as reportService from "../../../services/reportsService";

import type { RequestPerHourReportData } from "../../../shared/interface/Report/requestPerHourReport.interface";
import LocalStorageUtil from "../../../util/LocalStorageUtil";
import { LocalStorageConstants } from "../../../constants/ApplicationConstants/LocalStorageConstants";
import { UserInterface } from "../../../shared/interface/auth/User.interface";
import { ErrorMessageConstants } from "../../../constants/MessageConstants/ErrorMessageConstants";
import { ExportFile } from "../../../util/ExportFile";
import useValue from "../../../hooks/useValue";
import { General } from "../../../util/General";

export const useReportTable = () => {
  /* CONSTANT */
  const userData = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.USER_DATA) as UserInterface;

  /* STATE */
  const [reportData, setReportData] = useState<RequestPerHourReportData[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(moment().format("YYYY-MM-DD"));

  /* CUSTOM HOOKS */
  const { value: pdfIconLoading, handleDisableValue: handleDisablePdf, handleEnableValue: handleEnablePdf } = useValue(false);

  const { masterFilters, customerList, locationList } = useSelector((state: RootState) => state.common);

  /* HANDLERS */
  const handleDateChange = (date: Moment | null) => {
    if (date) {
      setSelectedDate(date.format("YYYY-MM-DD"));
    } else {
      setSelectedDate("");
    }
  };

  const fetchReportData = async () => {
    if (!selectedDate) {
      CustomNotification.showErrorMessage("Please select a date.");
      return;
    }

    if (!masterFilters?.customerId || !masterFilters?.locationId) {
      CustomNotification.showErrorMessage("Please select a customer and location.");
      return;
    }

    try {
      setLoading(true);
      const params = {
        date: selectedDate,
        customerId: masterFilters?.customerId,
        locationId: masterFilters?.locationId
      };

      const response = await reportService.getRequestPerHourReport(params);

      const formattedResult = response.data?.map((item, index) => ({
        ...item,
        sr_no: index + 1
      }));

      /* Check if the selected date is today */
      const isToday = selectedDate === moment().format("YYYY-MM-DD");
      const currentHour = moment().hour();

      /* Filter the data if the date is today */
      const filteredData = isToday ? formattedResult.filter((item) => parseInt(item.hour, 10) <= currentHour) : formattedResult;

      setReportData(filteredData);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDFFile = async () => {
    try {
      handleEnablePdf();

      if (!masterFilters?.customerId || !masterFilters?.locationId) {
        CustomNotification.showErrorMessage(ErrorMessageConstants.SELECT_CUSTOMER_LOCATION_FILTER);
        return;
      }

      const customer = customerList?.find((customer) => customer.id === masterFilters?.customerId);
      const location = locationList?.find((location) => location.id === masterFilters?.locationId);

      const { fromDate, toDate } = General.getFormattedDateRange(selectedDate);

      const headerInfo: any = {
        header: `${userData?.config?.theme?.HEADER_TEXT || ""}`,
        customerName: `${customer?.customer_name || ""}`,
        locationName: `${location?.location_name || ""}`,
        fromDate: fromDate ?? "-",
        toDate: toDate ?? "-"
      };

      const params = {
        date: selectedDate,
        customerId: masterFilters?.customerId,
        locationId: masterFilters?.locationId
      };

      const response = await reportService.getRequestPerHourReport(params);

      const allVisitorData =
        response?.data?.map((item, index) => ({
          "Sr No.": index + 1,
          Hour: item?.hour || "",
          Booking: item?.booking_count || "",
          Retrieval: item?.retrieval_count || ""
        })) || [];

      ExportFile.pdfFileDownload(allVisitorData, "Request Per Hour Report", headerInfo);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisablePdf();
    }
  };

  /* EFFECTS */
  useEffect(() => {
    fetchReportData();
  }, [masterFilters?.customerId, masterFilters?.locationId]);

  return {
    reportData,
    loading,
    handleDateChange,
    fetchReportData,
    selectedDate,
    pdfIconLoading,
    handleExportPDFFile
  };
};
